import type { AskChatResponse } from "@/types/AskChatResponse"
import type { ChatRequest } from "@/types/chatRequest"
import { getAccessToken, getRefreshPromise } from "@/lib/api"

/** Allow long LLM runs (retrieval + generation). Overridable via
 * VITE_CHAT_STREAM_TIMEOUT_MS for slow local models; defaults to 5 min. */
function resolveStreamTimeoutMs(): number {
  const raw = import.meta.env.VITE_CHAT_STREAM_TIMEOUT_MS
  const parsed = raw != null ? Number(raw) : NaN
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 5 * 60 * 1000
}

export const CHAT_STREAM_TIMEOUT_MS = resolveStreamTimeoutMs()

const ragBaseURL = (import.meta.env.VITE_RAG_API_URL ?? "").replace(/\/$/, "")

/** Combine the caller's abort signal (if any) with the stream timeout. */
function withTimeout(signal?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(CHAT_STREAM_TIMEOUT_MS)
  return signal ? AbortSignal.any([signal, timeout]) : timeout
}

export interface ChatStreamHandlers {
  onMeta?: (meta: { chat_id: string }) => void
  onToken: (content: string) => void
  /** Progress updates while the answer is being prepared (e.g. "Searching the tax guidance…"). */
  onStatus?: (status: { stage: string; text: string }) => void
  /** Model chain-of-thought deltas, streamed before the answer. Only emitted when
   * the backend's RAG_STREAM_THINKING flag is on (dev/local); absent in prod. */
  onThinking?: (content: string) => void
}

type StreamEvent =
  | { type: "meta"; chat_id: string }
  | { type: "token"; content: string }
  | { type: "thinking"; content: string }
  | { type: "status"; stage: string; text: string }
  | ({ type: "done" } & AskChatResponse)

/** #97 HARD RULE: chain-of-thought is a dev/local-only affordance. Only these
 * Vite build modes may surface it; any other mode (prod included) drops
 * `thinking` events on the floor even if a misconfigured backend sends them. */
const THINKING_ALLOWED_MODES = ["development", "dev", "local", "test"]
const thinkingAllowed = THINKING_ALLOWED_MODES.includes(import.meta.env.MODE)

async function fetchChatStream(
  body: ChatRequest,
  retryOn401: boolean,
  signal?: AbortSignal
): Promise<Response> {
  const url = ragBaseURL ? `${ragBaseURL}/chat` : "/chat"
  const token = getAccessToken()
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/x-ndjson",
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: withTimeout(signal),
  })

  if (response.status === 401 && retryOn401) {
    const newToken = await getRefreshPromise()
    if (!newToken) {
      throw new Error("Unauthorized")
    }
    headers.Authorization = `Bearer ${newToken}`
    return fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: withTimeout(signal),
    })
  }

  return response
}

async function readNdjsonStream(
  response: Response,
  handlers: ChatStreamHandlers
): Promise<AskChatResponse> {
  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error("Chat response had no body")
  }

  const decoder = new TextDecoder()
  let buffer = ""
  let donePayload: AskChatResponse | null = null

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let newlineIndex = buffer.indexOf("\n")
    while (newlineIndex >= 0) {
      const line = buffer.slice(0, newlineIndex).trim()
      buffer = buffer.slice(newlineIndex + 1)
      if (line) {
        const event = JSON.parse(line) as StreamEvent
        if (event.type === "meta" && event.chat_id) {
          handlers.onMeta?.({ chat_id: event.chat_id })
        } else if (event.type === "token" && event.content) {
          handlers.onToken(event.content)
        } else if (event.type === "thinking" && event.content) {
          if (thinkingAllowed) handlers.onThinking?.(event.content)
        } else if (event.type === "status" && event.text) {
          handlers.onStatus?.({ stage: event.stage, text: event.text })
        } else if (event.type === "done") {
          const { type, ...rest } = event
          void type
          donePayload = rest as AskChatResponse
        }
      }
      newlineIndex = buffer.indexOf("\n")
    }
  }

  const trailing = buffer.trim()
  if (trailing) {
    const event = JSON.parse(trailing) as StreamEvent
    if (event.type === "done") {
      const { type, ...rest } = event
      void type
      donePayload = rest as AskChatResponse
    }
  }

  if (!donePayload) {
    throw new Error("Chat stream ended before completion")
  }

  return donePayload
}

/** Stream POST /chat and invoke handlers as NDJSON events arrive. */
export async function streamChat(
  body: ChatRequest,
  handlers: ChatStreamHandlers,
  signal?: AbortSignal
): Promise<AskChatResponse> {
  const response = await fetchChatStream(body, true, signal)

  if (response.status === 401) {
    throw new Error("Unauthorized")
  }

  if (!response.ok) {
    let message = `Chat request failed (${response.status})`
    try {
      const errBody = (await response.json()) as { error?: string }
      if (errBody.error) message = errBody.error
    } catch {
      // ignore parse errors
    }
    throw new Error(message)
  }

  return readNdjsonStream(response, handlers)
}
